import React, { useState } from 'react';
import { PlusCircleIcon, XIcon, CalendarIcon, TagIcon } from 'lucide-react';
interface Step3DetailsProps {
  formData: any;
  updateFormData: (data: any) => void;
}
const Step3Details: React.FC<Step3DetailsProps> = ({
  formData,
  updateFormData
}) => {
  const [intakeDates, setIntakeDates] = useState<string[]>(formData.intakeDates?.length ? formData.intakeDates : []);
  const [qualifications, setQualifications] = useState<string[]>(formData.qualifications?.length ? formData.qualifications : []);
  const [newIntake, setNewIntake] = useState('');
  const [newQualification, setNewQualification] = useState('');
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    updateFormData({
      [name]: value
    });
  };
  // Intake dates management
  const handleAddIntake = () => {
    if (newIntake.trim() && !intakeDates.includes(newIntake.trim())) {
      const updatedIntakes = [...intakeDates, newIntake.trim()];
      setIntakeDates(updatedIntakes);
      updateFormData({
        intakeDates: updatedIntakes
      });
      setNewIntake('');
    }
  };
  const handleRemoveIntake = (intake: string) => {
    const updatedIntakes = intakeDates.filter(i => i !== intake);
    setIntakeDates(updatedIntakes);
    updateFormData({
      intakeDates: updatedIntakes
    });
  };
  const handleIntakeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddIntake();
    }
  };
  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString; // Return original string if not a valid date
      }
      // Format as Month Year (e.g., "September 2023")
      return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };
  // Qualifications management
  const handleAddQualification = () => {
    if (newQualification.trim() && !qualifications.includes(newQualification.trim())) {
      const updatedQualifications = [...qualifications, newQualification.trim()];
      setQualifications(updatedQualifications);
      updateFormData({
        qualifications: updatedQualifications
      });
      setNewQualification('');
    }
  };
  const handleRemoveQualification = (qualification: string) => {
    const updatedQualifications = qualifications.filter(q => q !== qualification);
    setQualifications(updatedQualifications);
    updateFormData({
      qualifications: updatedQualifications
    });
  };
  const handleQualificationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddQualification();
    }
  };
  // Campus locations
  const campusLocations = ['Dubai Main Campus', 'Abu Dhabi Campus', 'Sharjah Campus', 'Online Only', 'Multiple Locations'];
  return <div data-id="element-430">
      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-431">
        Program Details
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-432">
        Provide specific details about the program duration, dates, fees, and
        requirements.
      </p>
      <div className="space-y-6" data-id="element-433">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-434">
          <div data-id="element-435">
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-436">
              Program Duration <span className="text-red-500" data-id="element-437">*</span>
            </label>
            <input type="text" id="duration" name="duration" value={formData.duration || ''} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="e.g., 2 Years" required data-id="element-438" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-439">
              Specify the total duration of the program
            </p>
          </div>
          <div data-id="element-440">
            <label htmlFor="tuition" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-441">
              Tuition & Fees <span className="text-red-500" data-id="element-442">*</span>
            </label>
            <div className="relative" data-id="element-443">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-444">
                <span className="text-gray-500" data-id="element-445">AED</span>
              </div>
              <input type="text" id="tuition" name="tuition" value={formData.tuition || ''} onChange={handleInputChange} className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="e.g., 120,000" required data-id="element-446" />
            </div>
            <p className="text-xs text-gray-500 mt-1" data-id="element-447">
              Total program cost in AED
            </p>
          </div>
        </div>
        <div data-id="element-448">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-449">
            Start Dates / Intake Periods <span className="text-red-500" data-id="element-450">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-3" data-id="element-451">
            Add the available intake periods for this program.
          </p>
          <div className="flex flex-wrap items-center gap-2 mb-3" data-id="element-452">
            {intakeDates.map((intake, index) => <div key={index} className="bg-primary-50 text-primary-500 px-3 py-1 rounded-full text-sm flex items-center" data-id="element-453">
                <CalendarIcon className="w-3 h-3 mr-1" data-id="element-454" />
                {formatDate(intake)}
                <button type="button" onClick={() => handleRemoveIntake(intake)} className="ml-1 text-primary-500 hover:text-primary-600" data-id="element-455">
                  <XIcon className="w-4 h-4" data-id="element-456" />
                </button>
              </div>)}
            {intakeDates.length === 0 && <span className="text-sm text-gray-400" data-id="element-457">
                No intake periods added yet
              </span>}
          </div>
          <div className="flex" data-id="element-458">
            <input type="date" value={newIntake} onChange={e => setNewIntake(e.target.value)} onKeyDown={handleIntakeKeyDown} className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-459" />
            <button type="button" onClick={handleAddIntake} className="px-4 py-2 bg-primary-500 text-white rounded-r-md hover:bg-primary-600" data-id="element-460">
              Add
            </button>
          </div>
        </div>
        <div data-id="element-461">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-462">
            Required Qualifications <span className="text-red-500" data-id="element-463">*</span>
          </label>
          <p className="text-sm text-gray-500 mb-3" data-id="element-464">
            Specify the minimum qualifications required for admission.
          </p>
          <div className="flex flex-wrap items-center gap-2 mb-3" data-id="element-465">
            {qualifications.map((qualification, index) => <div key={index} className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm flex items-center" data-id="element-466">
                <TagIcon className="w-3 h-3 mr-1" data-id="element-467" />
                {qualification}
                <button type="button" onClick={() => handleRemoveQualification(qualification)} className="ml-1 text-gray-600 hover:text-gray-800" data-id="element-468">
                  <XIcon className="w-4 h-4" data-id="element-469" />
                </button>
              </div>)}
            {qualifications.length === 0 && <span className="text-sm text-gray-400" data-id="element-470">
                No qualifications added yet
              </span>}
          </div>
          <div className="flex" data-id="element-471">
            <input type="text" value={newQualification} onChange={e => setNewQualification(e.target.value)} onKeyDown={handleQualificationKeyDown} className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="e.g., Bachelor's degree" data-id="element-472" />
            <button type="button" onClick={handleAddQualification} className="px-4 py-2 bg-primary-500 text-white rounded-r-md hover:bg-primary-600" data-id="element-473">
              Add
            </button>
          </div>
        </div>
        <div data-id="element-474">
          <label htmlFor="accreditation" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-475">
            Accreditation Details
          </label>
          <textarea id="accreditation" name="accreditation" value={formData.accreditation || ''} onChange={handleInputChange} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" placeholder="e.g., Accredited by the Ministry of Education and AACSB International" data-id="element-476" />
          <p className="text-xs text-gray-500 mt-1" data-id="element-477">
            Include any relevant accreditation or certification information
          </p>
        </div>
        <div data-id="element-478">
          <label htmlFor="campusLocation" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-479">
            Campus Location <span className="text-red-500" data-id="element-480">*</span>
          </label>
          <select id="campusLocation" name="campusLocation" value={formData.campusLocation || ''} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" required data-id="element-481">
            <option value="" data-id="element-482">Select campus location</option>
            {campusLocations.map((location, index) => <option key={index} value={location} data-id="element-483">
                {location}
              </option>)}
          </select>
        </div>
      </div>
    </div>;
};
export default Step3Details;